import React, { useContext, useEffect, useState } from "react";

import { getAnalytics } from "../../services/clients";
import { AppContext } from "../../contexts/AppContext";
import Card from "../../components/Cards/Card";
import CardHeader from "../../components/Cards/CardHeader";
import CardBody from "../../components/Cards/CardBody";
import styled from "styled-components";
import { useWindowSize } from "../../hooks/useWindowSize";
import { formatSeconds } from "../../utils/utils";
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Line, Pie } from "react-chartjs-2";
import Loading from "../../components/Loading";

ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  PointElement,
  BarElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const CardContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex-grow: 0;
  backgroundColor: "var(--content-item-bg)";
  flex-shrink: 1;

  @media (min-width: 768px) {
    margin: 10px;
  }

  &.chart > div {
    width: 600px;
    max-width: 90vw;
  }
`;

export default function Dashboard() {
  const { selectedClient } = useContext(AppContext);
  const [fetchingAnalytics, setFetchingAnalytics] = useState(true);
  const [totalUsers, setTotalUsers] = useState(null);
  const [totalChatSessions, setTotalChatSessions] = useState(null);
  const [totalFeedback, setTotalFeedback] = useState(null);
  const [averageRating, setAverageRating] = useState(null);
  const [averageSession, setAverageSession] = useState(null);
  const [medianSession, setMedianSession] = useState(null);
  const [maxSession, setMaxSession] = useState(null);
  const [minSession, setMinSession] = useState(null);
  const [durationLabels, setDurationLabels] = useState([]);
  const [durationData, setDurationData] = useState({});
  const [totalDurations, setTotalDurations] = useState(null);
  const [usersPerDayData, setUsersPerDayData] = useState(null);
  const [videoStarts, setVideoStarts] = useState(null);
  const [videoLikes, setVideoLikes] = useState(null);
  const [videoResponseData, setVideoResponseData] = useState(null);
  const [videoResponseLabels, setVideoResponseLabels] = useState(null);
  const [videoCompleted, setVideoCompleted] = useState(null);
  const [totalLLMCost, setTotalLLMCost] = useState(null);
  const [inputLLMCost, setInputLLMCost] = useState(null);
  const [outputLLMCost, setOutputLLMCost] = useState(null);
  const [totalTokens, setTotalTokens] = useState(null);
  const [inputTokens, setInputTokens] = useState(null);
  const [outputTokens, setOutputTokens] = useState(null);
  const [totalLLMSessions, setTotalLLMSessions] = useState(null);
  const [goalYesTotal, setGoalYesTotal] = useState(null);
  const [goalNoTotal, setGoalNoTotal] = useState(null);
  const [goalsPerDayData, setGoalsPerDayData] = useState(null);
  const [videoEventsPerDayData, setVideoEventsPerDayData] = useState(null);
  const [totalPrompts, setTotalPrompts] = useState(null);
  const [promptsPerDayData, setPromptsPerDayData] = useState(null);
  const [chatSessionsPerDayData, setChatSessionsPerDayData] = useState(null);
  const [totalImages, setTotalImages] = useState(null);
  const [imagesPerDayData, setImagesPerDayData] = useState(null);
  const [totalAffirmations, setTotalAffirmations] = useState(null);
  const [affirmationsPerDayData, setAffirmationsPerDayData] = useState(null);
  const [emotionsPerDayData, setEmotionsPerDayData] = useState(null);
  const [totalEmotions, setTotalEmotions] = useState(null);
  const [emotionsData, setEmotionsData] = useState(null);
  const [platformUserData, setPlatformUserData] = useState(null);
  const [revenueCatAnalytics, setRevenueCatAnalytics] = useState(null);

  const [totalInterests, setTotalInterests] = useState(null);
  const [totalGoals, setTotalGoals] = useState(null);
  const [totalGoalActions, setTotalGoalActions] = useState(null);

  const [totalVideoResponses, setTotalVideoResponses] = useState(null);

  const [totalPagesViewed, setTotalPagesViewed] = useState(null);
  const [pagesViewedData, setPagesViewedData] = useState(null);

  const [welcomeVideoStarted, setWelcomeVideoStarted] = useState(null);
  const [welcomeVideoCompleted, setWelcomeVideoCompleted] = useState(null);

  const [iosUsers, setIosUsers] = useState(null);
  const [androidUsers, setAndroidUsers] = useState(null);
  const [unknownUsers, setUnknownUsers] = useState(null);

  const { width } = useWindowSize();

  const durationOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: `Session Durations (${totalDurations} total sessions)`,
      },
    },
  };

  const videoResponseOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: `Video Responses (${totalVideoResponses} total responses)`,
      },
    },
  };

  const pagesViewedOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: `Pages Viewed (${totalPagesViewed} pages)`,
      },
    },
  };

  const usersPerDayOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: `Active Users Per Day (${totalUsers} total users)`,
      },
    },
  };

  const goalsPerDayOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: `Goal Responses`,
      },
    },
  };

  const videoEventsPerDayOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: `Video Events`,
      },
    },
  };

  const promptsPerDayOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: `Prompt Responses (${totalPrompts} total prompts)`,
      },
    },
    scales: {
      y: {
        ticks: {
          callback: function(value) {
            return Number.isInteger(value) ? value : null;
          },
          stepSize: 1, // optional, helps guide spacing
        },
      },
    },
  };

  const chatSessionsPerDayOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: `Chat Session Per Day (${totalChatSessions} total chat sessions)`,
      },
    },
    scales: {
      y: {
        ticks: {
          callback: function(value) {
            return Number.isInteger(value) ? value : null;
          },
          stepSize: 1, // optional, helps guide spacing
          min: 0, // ensure y-axis starts at 0
        },
      },
    },
  };

  const imagesPerDayOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: `Image Uploads (${totalImages} total images)`,
      },
    },
    scales: {
      y: {
        ticks: {
          callback: function(value) {
            return Number.isInteger(value) ? value : null;
          },
          stepSize: 1, // optional, helps guide spacing
        },
      },
    },
  };

  const affirmationsPerDayOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: `Affirmation Likes (${totalAffirmations} total affirmations)`,
      },
    },
    scales: {
      y: {
        ticks: {
          callback: function(value) {
            return Number.isInteger(value) ? value : null;
          },
          stepSize: 1, // optional, helps guide spacing
        },
      },
    },
  };

  const emotionsPerDayOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: `Emotions Per Day (${totalEmotions} total emotions)`,
      },
    },
    scales: {
      y: {
        ticks: {
          callback: function(value) {
            return Number.isInteger(value) ? value : null;
          },
          stepSize: 1, // optional, helps guide spacing
        },
      },
    },
  };

  const emotionsOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: `Emotion Counts (${totalEmotions} pages)`,
      },
    },
  };

  const platformUserOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: `User Platfoms (${totalUsers} total users)`,
      },
    },
  };

  useEffect(() => {

    const controller = new AbortController();
    console.log("!!! USE EFFECT SELECTED CLIENT: ", selectedClient);
    const fetchData = async () => {
      const analytics = await getAnalytics(selectedClient.id, controller);
      setFetchingAnalytics(false);
      console.log("!!! ANALYTICS: ", analytics);
      setTotalUsers(analytics.total_users);

      const chatSessionData = analytics.chat_session_data;
      setTotalChatSessions(chatSessionData.total_chat_sessions);
      const chatSessionsPerDay = chatSessionData.chat_sessions_by_date;

      setTotalFeedback(analytics.total_feedback);
      setAverageRating(analytics.average_rating);
      setAverageSession(formatSeconds(analytics.average_session));
      setMedianSession(formatSeconds(analytics.median_session));
      setMaxSession(formatSeconds(analytics.max_session));
      setMinSession(formatSeconds(analytics.min_session));
      setTotalDurations(analytics.session_durations.length);

      const setupAnalytics = analytics.setup_analytics;
      setTotalInterests(setupAnalytics.total_interests);
      setTotalGoals(setupAnalytics.total_goals);
      setTotalGoalActions(setupAnalytics.total_goal_actions);

      const videoEvents = analytics.video_events;
      setVideoStarts(videoEvents["Video Started"]);
      setVideoLikes(videoEvents["Video Liked"]);
      setVideoLikes(analytics.video_likes);
      setVideoCompleted(videoEvents["Video Completed"]);

      const videoEventsPerDay = analytics.video_events_by_date;

      const platformAnalytics = analytics.platform_analytics;
      const iosUsers = platformAnalytics.ios;
      const androidUsers = platformAnalytics.android;
      const unknownUsers = analytics.total_users - iosUsers - androidUsers;
      setIosUsers(iosUsers);
      setAndroidUsers(androidUsers);
      setUnknownUsers(unknownUsers);

      const videoResponses = analytics.video_responses;
      console.log("!!! VIDEO RESPONSES: ", videoResponses);
      setTotalVideoResponses(videoResponses.total_video_responses);
      const videoResponseCounts = videoResponses.video_response_counts;

      const pagesViewed = analytics.page_view_data;
      const totalPagesViewed = Object.values(pagesViewed).reduce(
        (acc, curr) => acc + curr,
        0
      );
      setTotalPagesViewed(totalPagesViewed);

      const welcomeVideoData = analytics.welcome_video_data;
      setWelcomeVideoStarted(welcomeVideoData.started);
      setWelcomeVideoCompleted(welcomeVideoData.ended);

      const revenueCatAnalytics = {};

      analytics.revenue_cat_analytics.forEach((item) => {
        revenueCatAnalytics[item.id] = item.value;
      });
      setRevenueCatAnalytics(revenueCatAnalytics);

      const totalCost = analytics.llm_input_cost + analytics.llm_output_cost;
      const totalTokens =
        parseInt(analytics.llm_input_tokens) +
        parseInt(analytics.llm_output_tokens);
      setInputLLMCost(`$${analytics.llm_input_cost.toFixed(2)}`);
      setOutputLLMCost(`$${analytics.llm_output_cost.toFixed(2)}`);
      setTotalLLMCost(`$${totalCost.toFixed(2)}`);
      setTotalTokens(totalTokens.toLocaleString());
      setInputTokens(parseInt(analytics.llm_input_tokens).toLocaleString());
      setOutputTokens(parseInt(analytics.llm_output_tokens).toLocaleString());
      setTotalLLMSessions(
        parseInt(analytics.llm_session_count).toLocaleString()
      );

      const usersPerDay = analytics.users_per_day;
      const goalAnalytics = analytics.goal_analytics;
      setGoalYesTotal(goalAnalytics.total_yes);
      setGoalNoTotal(goalAnalytics.total_no);
      const goalsPerDay = goalAnalytics.goals_by_date;

      const promptAnalytics = analytics.prompt_analytics;
      setTotalPrompts(promptAnalytics.total_prompts);
      const promptsPerDay = promptAnalytics.prompts_by_date;

      const imageAnalytics = analytics.image_analytics;
      setTotalImages(imageAnalytics.total_images);
      const imagesPerDay = imageAnalytics.images_by_date;

      const affirmationAnalytics = analytics.affirmation_analytics;
      setTotalAffirmations(affirmationAnalytics.total_affirmations);
      const affirmationsPerDay = affirmationAnalytics.affirmations_by_date;

      const emotionAnalytics = analytics.emotion_analytics;
      setTotalEmotions(emotionAnalytics.total_emotions);
      const emotionsPerDay = emotionAnalytics.emotions_by_date;
      const emotions = emotionAnalytics.emotions;

      const durationLabels = [];
      for (let i = 0; i < analytics.session_durations.length; i++) {
        const duration = analytics.session_durations[i];
        const minutes = Math.floor(duration / 60);
        if (durationLabels.indexOf(minutes) === -1) {
          durationLabels.push(minutes);
        }
      }
      durationLabels.sort((a, b) => a - b);
      setDurationLabels(durationLabels);
      console.log("Duration Labels: ", durationLabels);
      const durationData = {
        labels: durationLabels,
        datasets: [
          {
            label: "Session Durations (minutes)",
            data: durationLabels.map((label) => {
              return analytics.session_durations.filter(
                (duration) => Math.floor(duration / 60) === label
              ).length;
            }),
            backgroundColor: "rgba(75, 192, 192, 0.5)",
          },
        ],
      };
      setDurationData(durationData);

      const videoResponseLabels = Object.keys(videoResponseCounts);
      setVideoResponseLabels(videoResponseLabels);

      const videoResponseData = {
        labels: videoResponseLabels,
        datasets: [
          {
            label: "Session Durations (minutes)",
            data: Object.values(videoResponseCounts),
            backgroundColor: "rgba(255, 99, 132, 0.5)",
            borderColor: "rgba(255, 99, 132, 1)",
          },
        ],
      };
      setVideoResponseData(videoResponseData);

      const pagesViewedData = {
        labels: Object.keys(pagesViewed),
        datasets: [
          {
            label: "Page Viewed",
            data: Object.values(pagesViewed),
            backgroundColor: "rgba(255, 99, 132, 0.5)",
            borderColor: "rgba(255, 99, 132, 1)",
          },
        ],
      };
      setPagesViewedData(pagesViewedData);

      const userPerDayLabels = Object.keys(usersPerDay);
      const userPerDayItems = Object.values(usersPerDay);
      console.log("User Per Day Items: ", userPerDayItems);
      console.log("User Per Day Labels: ", userPerDayLabels);

      const usersPerDayData = {
        labels: userPerDayLabels,
        datasets: [
          {
            label: "Users Per Day",
            data: userPerDayItems,
            backgroundColor: "rgba(75, 192, 192, 0.5)",
            borderColor: "rgba(75, 192, 192, 1)",
          },
        ],
      };

      console.log("Users Per Day Data: ", usersPerDayData);
      setUsersPerDayData(usersPerDayData);

      const goalsPerDayLabels = [];
      const goalYesPerDayTotal = [];
      const goalNoPerDayTotal = [];

      for (let i = 0; i < Object.keys(goalsPerDay).length; i++) {
        const date = Object.keys(goalsPerDay)[i];
        goalsPerDayLabels.push(date);
      }
      goalsPerDayLabels.sort((a, b) => a - b);

      for (let i = 0; i < goalsPerDayLabels.length; i++) {
        const date = goalsPerDayLabels[i];
        const goalYes = goalsPerDay[date]?.yes ?? 0;
        const goalNo = goalsPerDay[date]?.no ?? 0;
        goalYesPerDayTotal.push(goalYes);
        goalNoPerDayTotal.push(goalNo);
      }

      const goalsPerDayData = {
        labels: goalsPerDayLabels,
        datasets: [
          {
            label: "Yes",
            data: goalYesPerDayTotal,
            backgroundColor: "rgba(75, 192, 192, 0.5)",
            borderColor: "rgba(75, 192, 192, 1)",
          },
          {
            label: "No",
            data: goalNoPerDayTotal,
            backgroundColor: "rgba(255, 99, 132, 0.5)",
            borderColor: "rgba(255, 99, 132, 1)",
          },
        ],
      };

      console.log("Goals Per Day Data: ", goalsPerDayData);
      setGoalsPerDayData(goalsPerDayData);

      const videoEventsPerDayLabels = [];
      const videosStartedPerDayTotal = [];
      const videosCompletedPerDayTotal = [];
      const videosLikedPerDayTotal = [];
      const videoResponsesPerDayTotal = [];

      for (let i = 0; i < Object.keys(videoEventsPerDay).length; i++) {
        const date = Object.keys(videoEventsPerDay)[i];
        videoEventsPerDayLabels.push(date);
      }
      videoEventsPerDayLabels.sort((a, b) => a - b);

      for (let i = 0; i < videoEventsPerDayLabels.length; i++) {
        const date = videoEventsPerDayLabels[i];

        const videosStarted = videoEventsPerDay[date]?.["Video Started"] ?? 0;
        videosStartedPerDayTotal.push(videosStarted);
        const videosCompleted =
          videoEventsPerDay[date]?.["Video Completed"] ?? 0;
        videosCompletedPerDayTotal.push(videosCompleted);
        const videosLiked = videoEventsPerDay[date]?.["Video Liked"] ?? 0;
        videosLikedPerDayTotal.push(videosLiked);
        const videosResponse = videoEventsPerDay[date]?.["Video Response"] ?? 0;
        videoResponsesPerDayTotal.push(videosResponse);
      }

      console.log("!!! VIDEO EVENTS PER DAY LABELS: ", videoEventsPerDayLabels);

      const videoEventsPerDayData = {
        labels: videoEventsPerDayLabels,
        datasets: [
          {
            label: "Videos Started",
            data: videosStartedPerDayTotal,
            backgroundColor: "rgba(75, 192, 192, 0.5)",
            borderColor: "rgba(75, 192, 192, 1)",
          },
          {
            label: "Videos Completed",
            data: videosCompletedPerDayTotal,
            backgroundColor: "rgba(255, 99, 132, 0.5)",
            borderColor: "rgba(255, 99, 132, 1)",
          },
          {
            label: "Videos Liked",
            data: videosLikedPerDayTotal,
            backgroundColor: "rgba(255, 206, 86, 0.5)",
            borderColor: "rgba(255, 206, 86, 1)",
          },
          {
            label: "Video Responses",
            data: videoResponsesPerDayTotal,
            backgroundColor: "rgba(153, 102, 255, 0.5)",
            borderColor: "rgba(153, 102, 255, 1)",
          },
        ],
      };

      setVideoEventsPerDayData(videoEventsPerDayData);

      const chatSessionsPerDayLabels = [];
      const chatSessionsPerDayTotal = [];

      for (let i = 0; i < Object.keys(chatSessionsPerDay).length; i++) {
        const date = Object.keys(chatSessionsPerDay)[i];
        chatSessionsPerDayLabels.push(date);
      }
      chatSessionsPerDayLabels.sort((a, b) => a - b);

      for (let i = 0; i < chatSessionsPerDayLabels.length; i++) {
        const date = goalsPerDayLabels[i];
        chatSessionsPerDayTotal.push(chatSessionsPerDay[date]);
      }

      const chatSessionsPerDayData = {
        labels: chatSessionsPerDayLabels,
        datasets: [
          {
            label: "Chat Sessions Per Day",
            data: chatSessionsPerDayTotal,
            backgroundColor: "rgba(255, 99, 132, 0.5)",
            borderColor: "rgba(255, 99, 132, 1)",
          },
        ],
      };
      console.log("!!! CHAT SESSIONS PER DAY DATA: ", chatSessionsPerDayData);

      setChatSessionsPerDayData(chatSessionsPerDayData);

      const promptsPerDayLabels = [];
      const promptsPerDayTotal = [];

      for (let i = 0; i < Object.keys(promptsPerDay).length; i++) {
        const date = Object.keys(promptsPerDay)[i];
        promptsPerDayLabels.push(date);
      }
      promptsPerDayLabels.sort((a, b) => a - b);

      for (let i = 0; i < promptsPerDayLabels.length; i++) {
        const date = goalsPerDayLabels[i];
        promptsPerDayTotal.push(promptsPerDay[date]);
      }

      const promptsPerDayData = {
        labels: promptsPerDayLabels,
        datasets: [
          {
            label: "Prompts Per Day",
            data: promptsPerDayTotal,
            backgroundColor: "rgba(255, 99, 132, 0.5)",
            borderColor: "rgba(255, 99, 132, 1)",
          },
        ],
      };

      setPromptsPerDayData(promptsPerDayData);

      const imagesPerDayLabels = [];
      const imagesPerDayTotal = [];

      for (let i = 0; i < Object.keys(imagesPerDay).length; i++) {
        const date = Object.keys(imagesPerDay)[i];
        imagesPerDayLabels.push(date);
      }
      imagesPerDayLabels.sort((a, b) => a - b);

      for (let i = 0; i < imagesPerDayLabels.length; i++) {
        const date = goalsPerDayLabels[i];
        imagesPerDayTotal.push(imagesPerDay[date]);
      }

      const imagesPerDayData = {
        labels: imagesPerDayLabels,
        datasets: [
          {
            label: "Images Per Day",
            data: imagesPerDayTotal,
            backgroundColor: "rgba(255, 99, 132, 0.5)",
            borderColor: "rgba(255, 99, 132, 1)",
          },
        ],
      };

      setImagesPerDayData(imagesPerDayData);

      const affirmationsPerDayLabels = [];
      const affirmationsPerDayTotal = [];

      for (let i = 0; i < Object.keys(affirmationsPerDay).length; i++) {
        const date = Object.keys(affirmationsPerDay)[i];
        affirmationsPerDayLabels.push(date);
      }
      affirmationsPerDayLabels.sort((a, b) => a - b);

      for (let i = 0; i < affirmationsPerDayLabels.length; i++) {
        const date = goalsPerDayLabels[i];
        affirmationsPerDayTotal.push(affirmationsPerDay[date]);
      }

      const affirmationsPerDayData = {
        labels: affirmationsPerDayLabels,
        datasets: [
          {
            label: "Affirmations Per Day",
            data: affirmationsPerDayTotal,
            backgroundColor: "rgba(255, 99, 132, 0.5)",
            borderColor: "rgba(255, 99, 132, 1)",
          },
        ],
      };

      setAffirmationsPerDayData(affirmationsPerDayData);

      const emotionsPerDayLabels = [];
      const emotionsPerDayTotal = [];

      for (let i = 0; i < Object.keys(emotionsPerDay).length; i++) {
        const date = Object.keys(emotionsPerDay)[i];
        emotionsPerDayLabels.push(date);
      }
      emotionsPerDayLabels.sort((a, b) => a - b);

      for (let i = 0; i < emotionsPerDayLabels.length; i++) {
        const date = emotionsPerDayLabels[i];
        emotionsPerDayTotal.push(emotionsPerDay[date]);
      }

      const emotionsPerDayData = {
        labels: emotionsPerDayLabels,
        datasets: [
          {
            label: "Emotions Per Day",
            data: emotionsPerDayTotal,
            backgroundColor: "rgba(75, 192, 192, 0.5)",
            borderColor: "rgba(75, 192, 192, 1)",
          },
        ],
      };
      setEmotionsPerDayData(emotionsPerDayData);

      const emotionsData = {
        labels: Object.keys(emotions),
        datasets: [
          {
            label: "Emotions",
            data: Object.values(emotions),
            backgroundColor: "rgba(255, 99, 132, 0.5)",
            borderColor: "rgba(255, 99, 132, 1)",
          },
        ],
      };
      setEmotionsData(emotionsData);

      const platformUserData = {
        labels: ["iOS", "Android", "Unknown"],
        datasets: [
          {
            label: "Users",
            data: [iosUsers, androidUsers, unknownUsers],
            backgroundColor: [
              "rgba(255, 99, 132, 0.2)",
              "rgba(54, 162, 235, 0.2)",
              "rgba(255, 206, 86, 0.2)",
              "rgba(75, 192, 192, 0.2)",
              "rgba(153, 102, 255, 0.2)",
              "rgba(255, 159, 64, 0.2)",
            ],
            borderColor: [
              "rgba(255, 99, 132, 1)",
              "rgba(54, 162, 235, 1)",
              "rgba(255, 206, 86, 1)",
              "rgba(75, 192, 192, 1)",
              "rgba(153, 102, 255, 1)",
              "rgba(255, 159, 64, 1)",
            ],
          },
        ],
      };
      setPlatformUserData(platformUserData);
    };

    if (selectedClient?.id) {
      fetchData();
    }

    return () => {
      controller.abort();
    };

  }, [selectedClient]);

  if (fetchingAnalytics) {
    return <Loading>Loading Analytics...</Loading>;
  }

  return (
    <>
      <div
        className="relative flex  align-items-top"
        style={{
          backgroundColor: "var(--main-bg)",
          flexDirection: "row",
          alignItems: "center",
          flexGrow: 0,
          flexShrink: 0,
          flexWrap: "wrap",
          justifyContent: width <= 768 ? "space-evenly" : "flex-start",
        }}
      >
        <CardContainer>
          <Card>
            <CardHeader>Subscription Data</CardHeader>
            <CardBody>
              <div
                style={{
                  fontSize: "1.25rem",
                  fontWeight: "600",
                }}
              >
                Active Subscriptions:{" "}
                {revenueCatAnalytics?.active_subscriptions ?? ""}
              </div>
              <div
                style={{
                  fontSize: "1.25rem",
                  fontWeight: "600",
                }}
              >
                Active Trials: {revenueCatAnalytics?.active_trials ?? ""}
              </div>
              <div
                style={{
                  fontSize: "1.25rem",
                  fontWeight: "600",
                }}
              >
                MRR: ${revenueCatAnalytics?.mrr ?? "0.00"}
              </div>
              <div
                style={{
                  fontSize: "1.25rem",
                  fontWeight: "600",
                }}
              >
                Revenue: ${revenueCatAnalytics?.revenue ?? "0.00"}
              </div>
            </CardBody>
          </Card>
        </CardContainer>
        <CardContainer className="chart">
          <Card>
            {usersPerDayData && (
              <Line options={usersPerDayOptions} data={usersPerDayData} />
            )}
            <CardBody>
              <div style={{ fontSize: ".85rem", fontWeight: "600" }}>
                Total Interests: {totalInterests ?? "---"}
              </div>
              <div style={{ fontSize: ".85rem", fontWeight: "600" }}>
                Total Goals: {totalGoals ?? "---"}
              </div>
              <div style={{ fontSize: ".85rem", fontWeight: "600" }}>
                Total Goal Actions: {totalGoalActions ?? "---"}
              </div>
              <div style={{ fontSize: ".85rem", fontWeight: "600" }}>
                Welcome Video Started: {welcomeVideoStarted ?? "---"}
              </div>
              <div style={{ fontSize: ".85rem", fontWeight: "600" }}>
                Welcome Video Completed: {welcomeVideoCompleted ?? "---"}
              </div>
            </CardBody>
          </Card>
        </CardContainer>
        <CardContainer className="chart">
          <Card>
            {videoEventsPerDayData && (
              <>
                <Line
                  options={videoEventsPerDayOptions}
                  data={videoEventsPerDayData}
                />
                <CardBody>
                  <div style={{ fontSize: ".85rem", fontWeight: "600" }}>
                    Video Starts: {videoStarts}
                  </div>
                  <div style={{ fontSize: ".85rem", fontWeight: "600" }}>
                    Video Completes: {videoCompleted}
                  </div>
                  <div style={{ fontSize: ".85rem", fontWeight: "600" }}>
                    Videos Liked: {videoLikes}
                  </div>
                  <div style={{ fontSize: ".85rem", fontWeight: "600" }}>
                    Videos Responses: {totalVideoResponses}
                  </div>
                </CardBody>
              </>
            )}
          </Card>
        </CardContainer>
        <CardContainer className="chart">
          <Card>
            {goalsPerDayData && (
              <>
                <Line options={goalsPerDayOptions} data={goalsPerDayData} />
                <CardBody>
                  <div style={{ fontSize: ".85rem", fontWeight: "600" }}>
                    Total Yes: {goalYesTotal}
                  </div>
                  <div style={{ fontSize: ".85rem", fontWeight: "600" }}>
                    Total No: {goalNoTotal}
                  </div>
                </CardBody>
              </>
            )}
          </Card>
        </CardContainer>
        <CardContainer className="chart">
          <Card>
            {promptsPerDayData && (
              <Line options={promptsPerDayOptions} data={promptsPerDayData} />
            )}
          </Card>
        </CardContainer>
        <CardContainer className="chart">
          <Card>
            {emotionsPerDayData && (
              <Line options={emotionsPerDayOptions} data={emotionsPerDayData} />
            )}
          </Card>
        </CardContainer>
        <CardContainer className="chart">
          <Card>
            {emotionsData && (
              <Bar options={emotionsOptions} data={emotionsData} />
            )}
          </Card>
        </CardContainer>
        <CardContainer className="chart">
          <Card>
            {imagesPerDayData && (
              <Line options={imagesPerDayOptions} data={imagesPerDayData} />
            )}
          </Card>
        </CardContainer>
        <CardContainer className="chart">
          <Card>
            {affirmationsPerDayData && (
              <Line
                options={affirmationsPerDayOptions}
                data={affirmationsPerDayData}
              />
            )}
          </Card>
        </CardContainer>
        <CardContainer className="chart">
          <Card>
            {durationLabels.length > 0 && (
              <>
                <Bar options={durationOptions} data={durationData} />
                <CardBody>
                  <div style={{ fontSize: ".85rem", fontWeight: "600" }}>
                    Average Session: {averageSession}
                  </div>
                  <div style={{ fontSize: ".85rem", fontWeight: "600" }}>
                    Median Session: {medianSession}
                  </div>
                  <div style={{ fontSize: ".85rem", fontWeight: "600" }}>
                    Min Session: {minSession}
                  </div>
                  <div style={{ fontSize: ".85rem", fontWeight: "600" }}>
                    Max Session: {maxSession}
                  </div>
                </CardBody>
              </>
            )}
          </Card>
        </CardContainer>
        <CardContainer className="chart">
          <Card>
            {chatSessionsPerDayData && (
              <Line
                options={chatSessionsPerDayOptions}
                data={chatSessionsPerDayData}
              />
            )}
            <CardBody>
              <div style={{ fontSize: ".85rem", fontWeight: "600" }}>
                Feedback Count: {totalFeedback ?? "---"}
              </div>
              <div style={{ fontSize: ".85rem", fontWeight: "600" }}>
                Average Rating: {averageRating ?? "---"}
              </div>
            </CardBody>
          </Card>
        </CardContainer>
        <CardContainer className="chart">
          <Card>
            {videoResponseLabels && (
              <>
                <Bar options={videoResponseOptions} data={videoResponseData} />
                <CardBody>
                  <div style={{ fontSize: ".85rem", fontWeight: "600" }}>
                    Video Starts: {videoStarts}
                  </div>
                  <div style={{ fontSize: ".85rem", fontWeight: "600" }}>
                    Video Completes: {videoCompleted}
                  </div>
                  <div style={{ fontSize: ".85rem", fontWeight: "600" }}>
                    Videos Liked: {videoLikes}
                  </div>
                </CardBody>
              </>
            )}
          </Card>
        </CardContainer>
        <CardContainer className="chart">
          <Card>
            {pagesViewedData && (
              <Bar options={pagesViewedOptions} data={pagesViewedData} />
            )}
          </Card>
        </CardContainer>
        <CardContainer className="chart">
          <Card>
            {platformUserData && (
              <Pie data={platformUserData} options={platformUserOptions} />
            )}
            <CardBody>
              <div style={{ fontSize: ".85rem", fontWeight: "600" }}>
                iOS Users: {iosUsers ?? "---"} (
                {((100 * iosUsers) / totalUsers).toFixed(2)}%)
              </div>
              <div style={{ fontSize: ".85rem", fontWeight: "600" }}>
                Android Users: {androidUsers ?? "---"} (
                {((100 * androidUsers) / totalUsers).toFixed(2)}%)
              </div>
              <div style={{ fontSize: ".85rem", fontWeight: "600" }}>
                Unknown Users: {unknownUsers ?? "---"} (
                {((100 * unknownUsers) / totalUsers).toFixed(2)}%)
              </div>
            </CardBody>
          </Card>
        </CardContainer>
        <CardContainer>
          <Card>
            <CardHeader>LLM Usage</CardHeader>
            <CardBody>
              <div
                style={{
                  fontSize: "1.25rem",
                  fontWeight: "600",
                  textDecoration: "underline",
                }}
              >
                Total Cost: {totalLLMCost ?? "---"}
              </div>
              <div style={{ fontSize: "1rem", fontWeight: "600" }}>
                Input Cost: {inputLLMCost ?? "---"}
              </div>
              <div style={{ fontSize: "1rem", fontWeight: "600" }}>
                Output Cost: {outputLLMCost ?? "---"}
              </div>
              <div
                style={{
                  fontSize: "1.25rem",
                  fontWeight: "600",
                  textDecoration: "underline",
                }}
              >
                Total Tokens: {totalTokens ?? "---"}
              </div>
              <div style={{ fontSize: "1rem", fontWeight: "600" }}>
                Input Tokens: {inputTokens ?? "---"}
              </div>
              <div style={{ fontSize: "1rem", fontWeight: "600" }}>
                Output Tokens: {outputTokens ?? "---"}
              </div>
              <div
                style={{
                  fontSize: "1.25rem",
                  fontWeight: "600",
                  textDecoration: "underline",
                }}
              >
                {totalLLMSessions ?? ""} Sessions
              </div>
            </CardBody>
          </Card>
        </CardContainer>
      </div>
    </>
  );
}
